import '../Styles/CommandStatusStrip.css';
import useSnapshotStreamingStore from '../Stores/SnapshotStreamingStore';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
import useMeicWebsocketDataStore from '../Stores/MEIC_Data_Store';
import { useMeicSummary } from './meic/useMeicSummary';

function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatSignedNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  const number = Number(value);
  const sign = number > 0 ? '+' : '';

  return `${sign}${number.toFixed(2)}`;
}

function formatSignedPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  const number = Number(value);
  const sign = number > 0 ? '+' : '';

  return `${sign}${number.toFixed(2)}%`;
}

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  const number = Number(value);
  const sign = number > 0 ? '+' : number < 0 ? '-' : '';

  return `${sign}$${Math.abs(number).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function getStrategyClosedTodayPnl(closedToday) {
  if (!Array.isArray(closedToday) || closedToday.length === 0) {
    return 0;
  }

  return closedToday.reduce((sum, trade) => {
    const pnl =
      trade?.realized_pnl ??
      trade?.realizedPnl ??
      trade?.pnl ??
      trade?.profit_loss ??
      0;

    return sum + Number(pnl || 0);
  }, 0);
}

function getCurrentEtMinutes() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const hour = Number(parts.find(part => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find(part => part.type === 'minute')?.value ?? 0);

  return hour * 60 + minute;
}

function isAfterMarketClose() {
  return getCurrentEtMinutes() >= 16 * 60;
}

function areAllMeicTradesClosed(trades) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return false;
  }

  return trades.every(trade => {
    return (
      String(trade?.status || '').toUpperCase() === 'CLOSED' ||
      Boolean(trade?.outcome)
    );
  });
}

function getMeicTradesPnl(trades) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return 0;
  }

  return trades.reduce((sum, trade) => {
    const pnl =
      trade?.pnlDollars ??
      trade?.net_profit_loss ??
      trade?.profit_loss ??
      0;

    return sum + Number(pnl || 0);
  }, 0);
}

function getMeicEodPnl({ summaryStats, liveTradeRows }) {
  /*
    Source 1:
    Use the live daily_summary if it is ever received while the page is open.

    Source 2:
    If daily_summary is not replayed on page load, use the merged MEIC rows
    from useMeicSummary after market close, but only when all MEIC rows are closed.
  */
  if (summaryStats) {
    return Number(summaryStats.totalRealizedPnlDollars || 0);
  }

  if (!isAfterMarketClose()) {
    return 0;
  }

  if (!areAllMeicTradesClosed(liveTradeRows)) {
    return 0;
  }

  return getMeicTradesPnl(liveTradeRows);
}

function getMarketStateDescription(marketState) {
  if (!marketState) return 'Awaiting market state';

  const gexSign = marketState.gexSign;
  const volTrend = marketState.volTrend;

  if (!gexSign && !volTrend) return 'Awaiting market state';

  const formattedGex =
    gexSign === 'positive'
      ? 'Positive MS GEX'
      : gexSign === 'negative'
        ? 'Negative MS GEX'
        : `${gexSign} GEX`;

  const formattedVol =
    volTrend === 'increasing'
      ? 'Rising Vol'
      : volTrend === 'decreasing'
        ? 'Falling Vol'
        : volTrend;

  return `${formattedGex} / ${formattedVol}`;
}

export default function CommandStatusStrip() {
  const spxSnapshot = useSnapshotStreamingStore(state => state.spxSnapshot);

  const openPositions = useStrategyDashboardStore(
    state => state.tradeTracking.openPositions
  );

  const strategyClosedToday = useStrategyDashboardStore(
    state => state.tradeTracking.closedToday
  );

  const meicMarketState = useMeicWebsocketDataStore(
    state => state.marketState
  );

  const {
    summaryStats,
    liveTradeRows,
  } = useMeicSummary();

  const openPositionCount = Array.isArray(openPositions)
    ? openPositions.length
    : 0;

  const strategyClosedPnl = getStrategyClosedTodayPnl(strategyClosedToday);

  const meicEodPnl = getMeicEodPnl({
    summaryStats,
    liveTradeRows,
  });

  const dailyPnl = strategyClosedPnl + meicEodPnl;

  const spxChangeClass =
    Number(spxSnapshot?.change) >= 0 ? 'positive' : 'negative';

  const dailyPnlClass =
    Number(dailyPnl) >= 0 ? 'positive' : 'negative';

  const dailyPnlSubtitle = 'ACCE/GEMIC/ARGOS/MEIC';

  return (
    <section className="commandStatusStrip">
      <div className="statusTile">
        <span>SPX</span>
        <strong>{formatNumber(spxSnapshot?.current)}</strong>
        <small className={spxChangeClass}>
          {formatSignedNumber(spxSnapshot?.change)} /{' '}
          {formatSignedPercent(spxSnapshot?.changePercent)}
        </small>
      </div>

      <div className="statusTile">
        <span>Active Positions</span>
        <strong>{openPositionCount}</strong>
        <small>Across open strategies</small>
      </div>

      <div className="statusTile">
        <span>Daily P/L</span>
        <strong className={dailyPnlClass}>
          {formatMoney(dailyPnl)}
        </strong>
        <small>{dailyPnlSubtitle}</small>
      </div>

      <div className="statusTile">
        <span>Market State</span>
        <strong>
          {meicMarketState?.regime !== null &&
          meicMarketState?.regime !== undefined
            ? `Regime ${meicMarketState.regime}`
            : '—'}
        </strong>
        <small>{getMarketStateDescription(meicMarketState)}</small>
      </div>
    </section>
  );
}



// import '../Styles/CommandStatusStrip.css';
// import useSnapshotStreamingStore from '../Stores/SnapshotStreamingStore';
// import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
// import useMeicWebsocketDataStore from '../Stores/MEIC_Data_Store';
//
// function formatNumber(value) {
//   if (value === null || value === undefined || Number.isNaN(Number(value))) {
//     return '—';
//   }
//
//   return Number(value).toLocaleString(undefined, {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });
// }
//
// function formatSignedNumber(value) {
//   if (value === null || value === undefined || Number.isNaN(Number(value))) {
//     return '—';
//   }
//
//   const number = Number(value);
//   const sign = number > 0 ? '+' : '';
//
//   return `${sign}${number.toFixed(2)}`;
// }
//
// function formatSignedPercent(value) {
//   if (value === null || value === undefined || Number.isNaN(Number(value))) {
//     return '—';
//   }
//
//   const number = Number(value);
//   const sign = number > 0 ? '+' : '';
//
//   return `${sign}${number.toFixed(2)}%`;
// }
//
// function formatMoney(value) {
//   if (value === null || value === undefined || Number.isNaN(Number(value))) {
//     return '—';
//   }
//
//   const number = Number(value);
//   const sign = number > 0 ? '+' : number < 0 ? '-' : '';
//
//   return `${sign}$${Math.abs(number).toLocaleString(undefined, {
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   })}`;
// }
//
// function getStrategyClosedTodayPnl(closedToday) {
//   if (!Array.isArray(closedToday) || closedToday.length === 0) {
//     return 0;
//   }
//
//   return closedToday.reduce((sum, trade) => {
//     const pnl =
//       trade?.realized_pnl ??
//       trade?.realizedPnl ??
//       trade?.pnl ??
//       trade?.profit_loss ??
//       0;
//
//     return sum + Number(pnl || 0);
//   }, 0);
// }
//
// function getMeicEodPnl(dailySummary) {
//   if (!dailySummary) return 0;
//
//   return Number(dailySummary.totalRealizedPnlDollars || 0);
// }
//
// function getMarketStateDescription(marketState) {
//   if (!marketState) return 'Awaiting market state';
//
//   const gexSign = marketState.gexSign;
//   const volTrend = marketState.volTrend;
//
//   if (!gexSign && !volTrend) return 'Awaiting market state';
//
//   const formattedGex =
//     gexSign === 'positive'
//       ? 'Positive MS GEX'
//       : gexSign === 'negative'
//         ? 'Negative MS GEX'
//         : `${gexSign} GEX`;
//
//   const formattedVol =
//     volTrend === 'increasing'
//       ? 'Rising Vol'
//       : volTrend === 'decreasing'
//         ? 'Falling Vol'
//         : volTrend;
//
//   return `${formattedGex} / ${formattedVol}`;
// }
//
// export default function CommandStatusStrip() {
//   const spxSnapshot = useSnapshotStreamingStore(state => state.spxSnapshot);
//
//   const openPositions = useStrategyDashboardStore(
//     state => state.tradeTracking.openPositions
//   );
//
//   const strategyClosedToday = useStrategyDashboardStore(
//     state => state.tradeTracking.closedToday
//   );
//
//   const meicMarketState = useMeicWebsocketDataStore(
//     state => state.marketState
//   );
//
//   const meicDailySummary = useMeicWebsocketDataStore(
//     state => state.dailySummary
//   );
//
//   const openPositionCount = Array.isArray(openPositions)
//     ? openPositions.length
//     : 0;
//
//   const strategyClosedPnl = getStrategyClosedTodayPnl(strategyClosedToday);
//   const meicEodPnl = getMeicEodPnl(meicDailySummary);
//
//   const dailyPnl = strategyClosedPnl + meicEodPnl;
//
//   const spxChangeClass =
//     Number(spxSnapshot?.change) >= 0 ? 'positive' : 'negative';
//
//   const dailyPnlClass =
//     Number(dailyPnl) >= 0 ? 'positive' : 'negative';
//
//   const dailyPnlSubtitle = meicDailySummary
//     ? 'EOD realized strategy P/L'
//     : 'ACCE/GEMIC/ARGOS/MEIC';
//
//   return (
//     <section className="commandStatusStrip">
//       <div className="statusTile">
//         <span>SPX</span>
//         <strong>{formatNumber(spxSnapshot?.current)}</strong>
//         <small className={spxChangeClass}>
//           {formatSignedNumber(spxSnapshot?.change)} /{' '}
//           {formatSignedPercent(spxSnapshot?.changePercent)}
//         </small>
//       </div>
//
//       <div className="statusTile">
//         <span>Active Positions</span>
//         <strong>{openPositionCount}</strong>
//         <small>Across open strategies</small>
//       </div>
//
//       <div className="statusTile">
//         <span>Daily P/L</span>
//         <strong className={dailyPnlClass}>
//           {formatMoney(dailyPnl)}
//         </strong>
//         <small>{dailyPnlSubtitle}</small>
//       </div>
//
//       <div className="statusTile">
//         <span>Market State</span>
//         <strong>
//           {meicMarketState?.regime !== null &&
//           meicMarketState?.regime !== undefined
//             ? `Regime ${meicMarketState.regime}`
//             : '—'}
//         </strong>
//         <small>{getMarketStateDescription(meicMarketState)}</small>
//       </div>
//     </section>
//   );
// }